"use client";

import { useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Switch } from "@/components/ui/switch";
import { Button } from "@/components/ui/button";
import {
	AlertDialog,
	AlertDialogAction,
	AlertDialogCancel,
	AlertDialogContent,
	AlertDialogDescription,
	AlertDialogFooter,
	AlertDialogHeader,
	AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Loader2, Trash2 } from "lucide-react";
import { useAppSettings } from "@/hooks/useAppSettings";
import { settingsApi } from "@/lib/settings-api.service";
import { Toast } from "@/lib/utils";

/**
 * Admin editor for global (not per-khoa) app settings. Today that is only
 * `allowPrintBeforeCompletion`, which gates the print dialog that
 * `student-details.tsx` shows before "Tiếp nhận" — see that component for
 * the read side.
 *
 * Cùng chỗ này còn có nút xoá cache thống kê, vì nó cũng là một cần gạt toàn
 * cục của hệ thống chứ không thuộc về dữ liệu của khoa nào.
 */
export default function AppSettingsManagement() {
	const { settings, setSettings, loading } = useAppSettings();
	const [saving, setSaving] = useState(false);
	const [confirmResetOpen, setConfirmResetOpen] = useState(false);
	const [resetting, setResetting] = useState(false);

	const handleResetCache = async () => {
		setResetting(true);
		try {
			await settingsApi.resetCache();
			setConfirmResetOpen(false);
			Toast.success("Đã xoá cache. Số liệu sẽ được tính lại ở lần xem tới.");
		} catch (error) {
			Toast.error(error instanceof Error ? error.message : "Không thể xoá cache");
		} finally {
			setResetting(false);
		}
	};

	const handleToggle = async (allowPrintBeforeCompletion: boolean) => {
		setSaving(true);
		try {
			setSettings(await settingsApi.update({ allowPrintBeforeCompletion }));
			Toast.success("Đã lưu cài đặt");
		} catch (error) {
			Toast.error(error instanceof Error ? error.message : "Không thể lưu cài đặt");
		} finally {
			setSaving(false);
		}
	};

	if (loading) {
		return (
			<div className="space-y-4">
				<Card>
					<CardContent className="p-4 flex items-center justify-between gap-3">
						<Skeleton className="h-5 w-64" />
						<Skeleton className="h-5 w-9 rounded-full" />
					</CardContent>
				</Card>
				<Card>
					<CardContent className="p-4 flex items-center justify-between gap-3">
						<Skeleton className="h-5 w-72" />
						<Skeleton className="h-9 w-32" />
					</CardContent>
				</Card>
			</div>
		);
	}

	return (
		<div className="space-y-4">
			<Card>
				<CardContent className="p-4 flex items-center justify-between gap-3">
					<div className="space-y-1">
						<label htmlFor="allow-print-before-completion" className="font-medium text-foreground">
							In giấy báo khi tiếp nhận
						</label>
						<p className="text-sm text-muted-foreground">
							Khi bật, bấm "Tiếp nhận" sẽ cập nhật hồ sơ rồi mở hộp thoại in, nên giấy in mang tên
							người tiếp nhận, thời gian tiếp nhận và trạng thái mới. Khi tắt, hồ sơ được tiếp nhận
							ngay, không in.
						</p>
					</div>
					<div className="flex items-center gap-2">
						{saving && <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />}
						<Switch
							id="allow-print-before-completion"
							checked={settings.allowPrintBeforeCompletion}
							disabled={saving}
							onCheckedChange={handleToggle}
						/>
					</div>
				</CardContent>
			</Card>

			<Card>
				<CardContent className="p-4 flex items-center justify-between gap-3">
					<div className="space-y-1">
						<p className="font-medium text-foreground">Xoá cache thống kê</p>
						<p className="text-sm text-muted-foreground">
							Trang thống kê giữ kết quả trong hai phút để đỡ tải cho hệ thống. Nếu dữ liệu vừa được
							sửa thẳng dưới database hoặc bằng script, bấm nút này để buộc tính lại ngay thay vì
							chờ hết hạn.
						</p>
					</div>
					<AlertDialog open={confirmResetOpen} onOpenChange={setConfirmResetOpen}>
						<Button
							variant="outline"
							className="flex-shrink-0"
							disabled={resetting}
							onClick={() => setConfirmResetOpen(true)}
						>
							{resetting ? (
								<Loader2 className="h-4 w-4 animate-spin" />
							) : (
								<Trash2 className="h-4 w-4" />
							)}
							{resetting ? "Đang xoá..." : "Xoá cache"}
						</Button>
						<AlertDialogContent>
							<AlertDialogHeader>
								<AlertDialogTitle>Xoá cache thống kê?</AlertDialogTitle>
								<AlertDialogDescription>
									Mọi số liệu thống kê sẽ được tính lại từ database ở lần xem tiếp theo. Không mất
									dữ liệu, nhưng vài truy vấn đầu sẽ chậm hơn bình thường.
								</AlertDialogDescription>
							</AlertDialogHeader>
							<AlertDialogFooter>
								<AlertDialogCancel disabled={resetting}>Huỷ</AlertDialogCancel>
								<AlertDialogAction
									disabled={resetting}
									onClick={(e) => {
										e.preventDefault();
										handleResetCache();
									}}
								>
									{resetting ? (
										<>
											<Loader2 className="h-4 w-4 animate-spin" />
											Đang xoá...
										</>
									) : (
										"Xoá cache"
									)}
								</AlertDialogAction>
							</AlertDialogFooter>
						</AlertDialogContent>
					</AlertDialog>
				</CardContent>
			</Card>
		</div>
	);
}
